
import type { PropType, Ref } from 'vue'

import { ref, computed, defineComponent } from 'vue'

import Loader from '@/common/components/UI/Loader/index.vue'

type DwSelectValue = number | null
type DwSelectOption = Record<string, unknown>

import { useOnOutsidePress } from 'vue-composable'

export default defineComponent({
    components: {
        'loader': Loader
    },
    props: {
        value: {
            type: [Number, null] as PropType<DwSelectValue>,
            default: null
        },
        placeholder: {
            type: [String, null] as PropType<string | null>,
            default: null
        },
        options: {
            type: Array as PropType<Array<DwSelectOption>>,
            default: () => []
        },
        optionLabelKey: {
            type: String,
            default: 'name'
        },
        selectBy: {
            type: String,
            default: 'id'
        },
        loading: {
            type: Boolean,
            default: false,
        },
        failed: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        bordered: {
            type: Boolean,
            default: false,
        }
    },
    emits: ['update:value', 'change'],
    setup(props, { emit }) {
        const elementref: Ref<HTMLElement | null> = ref(null)
        const is_opened = ref(false)

        const isOptionSelected = (option: DwSelectOption): boolean => {
            if (props.value === null) {
                return false
            }

            return props.value === option.id
        }

        const selectOption = (option: DwSelectOption) => {
            if (props.disabled) return

            const selectedValue = option[props.selectBy]
            emit('update:value', selectedValue)
            emit('change', selectedValue)

            closeOptions()
        }

        const openOptions = () => {
            is_opened.value = true
        }

        const closeOptions = () => {
            is_opened.value = false
        }

        useOnOutsidePress(elementref, closeOptions)

        const toggleOptions = () => {
            is_opened.value = !is_opened.value
        }

        const valueLabel = computed(() => {
            if (Boolean(props.value)) {
                const option = props.options.find((option) => option[props.selectBy] === props.value)

                if (option) {
                    return option[props.optionLabelKey]
                }
            }

            return null
        })

        const isValueShown = computed(() => Boolean(props.value))
        const isPlaceholderShown = computed(() => !Boolean(props.value))

        return {
            elementref,
            is_opened,
            isOptionSelected,
            selectOption,
            openOptions,
            closeOptions,
            toggleOptions,
            valueLabel,
            isValueShown,
            isPlaceholderShown
        }
    }
})
